import React from 'react';
import styled, { css } from 'styled-components';
import { DefaultTheme } from 'styled-components';
import { focusRing, primitiveColors, font, padding, radius } from '../../../../styles/template';

/**
 * Применяет переданные стили:
 *   • на :hover — для устройств с реальным указателем (мышь/трекпад);
 *   • на :active — для touch-устройств, где :hover «залипает» после тапа.
 * Таким образом визуальный «hover-эффект» срабатывает только в момент
 * нажатия пальцем и возвращается в исходное состояние при отпускании.
 */
const hoverable = (
    styles: ReturnType<typeof css>,
    pseudo: '' | ':before' | ':after' = ''
) => css`
    @media (hover: hover) {
        &:hover${pseudo} {
            ${styles}
        }
    }

    @media (hover: none) {
        &:active${pseudo} {
            ${styles}
        }
    }
`;

type PropsType = {
    accent?: boolean
    disabled?: boolean
    line?: boolean
    transparent?: boolean
    middle?: boolean
    small?: boolean
    light?: boolean
    secondary?: boolean
    success?: boolean
    link?: boolean
    onClick?: React.MouseEventHandler<HTMLElement>
    autoWidth?: boolean
    /** HTML type атрибут кнопки */
    type?: 'button' | 'submit' | 'reset'
    isLoading?: boolean
    children: React.ReactNode
    href?: string
    dotted?: boolean
    target?: string
    rel?: string
}

const BUTTON_STYLE_PROPS = [
    'accent',
    'line',
    'transparent',
    'middle',
    'small',
    'light',
    'secondary',
    'success',
    'link',
    'autoWidth',
    'dotted',
    'isLoading',
] as const;

const Wrap = styled.button.withConfig({
    shouldForwardProp: (prop) => !BUTTON_STYLE_PROPS.includes(prop as (typeof BUTTON_STYLE_PROPS)[number]),
})<PropsType>`
    color: ${primitiveColors.gray[50]};
    border-radius: ${(p) => p.theme.components?.Button?.radius ?? p.theme.primitives?.radius?.sm ?? radius.normal};
    padding: 0 ${(p) => p.theme.primitives?.spacing?.[2] ?? padding.small};
    ${font.accentM};
    position: relative;
    width: 100%;
    height: 7rem;
    text-decoration: none;
    white-space: nowrap;
    text-align: center;
    border: none;
    -webkit-backface-visibility: hidden;
    -moz-osx-font-smoothing: grayscale;
    cursor: pointer;
    background: none;
    z-index: 1;
    max-width: 100%;
    display: flex;
    gap: 1rem;
    justify-items: center;
    align-items: center;
    justify-content: center;
    align-content: center;
    transition: all 0.3s ease;

    &:active {
        transform: translateY(0.2rem);
    }
  
    &:before, &:after {
        content: '';
        z-index: -1;
        border-radius: inherit;
        pointer-events: none;
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        -webkit-backface-visibility: hidden;
        transition: opacity 0.3s, -webkit-transform .3s;
        transition: transform 0.3s, opacity .3s;
        transition: transform 0.3s, opacity 0.3s, -webkit-transform .3s;
        transition-timing-function: cubic-bezier(0.75, 0, 0.125, 1);
    }
  
    &:before {
        border-width: 3px;
        border-style: solid;
        opacity: 0;
        -webkit-transform: scale3d(1, 1, 1);
        transform: scale3d(1, 1, 1);
    }
    
    &:focus {
        text-decoration: none;
        outline: none;
    }

    ${focusRing}

    ${hoverable(css`
        text-decoration: none;
        outline: none;
    `)}

    ${hoverable(css`
        opacity: 1;
        -webkit-transform: scale3d(1, 1, 1);
        transform: scale3d(1, 1, 1);
    `, ':before')}

    ${hoverable(css`
        opacity: 0;
        -webkit-transform: scale3d(0.8, 0.8, 1);
        transform: scale3d(0.8, 0.8, 1);
    `, ':after')}

    &:before {
        border-color: ${({ theme }: { theme: DefaultTheme }) => theme.brand?.primary ?? primitiveColors.primary[500]};
    }
    
    &:after {
        background-color: ${({ theme }: { theme: DefaultTheme }) => theme.brand?.primary ?? primitiveColors.primary[500]};
    }

    ${hoverable(css`
        color: ${({ theme }: { theme: DefaultTheme }) => theme.brand?.primary ?? primitiveColors.primary[500]};
        && .colorFill {
            fill: ${({ theme }: { theme: DefaultTheme }) => theme.brand?.primary ?? primitiveColors.primary[500]};
        }
        && .colorStroke {
            stroke: ${({ theme }: { theme: DefaultTheme }) => theme.brand?.primary ?? primitiveColors.primary[500]};
        }
    `)}
    
    && .colorFill {
        fill: ${primitiveColors.gray[50]};
    }
    
    && .colorStroke {
        stroke: ${primitiveColors.gray[50]};
    }

    ${({ autoWidth }) => autoWidth && css`
         width: fit-content;
    `}

    ${({ theme, disabled }) => disabled && css`
        color: ${theme.semantic.text.secondary} !important;
      
        && .colorFill {
            fill: ${theme.semantic.text.secondary} !important;
        }
      
        && .colorStroke {
            stroke: ${theme.semantic.text.secondary} !important;
        }
        
        &:before {
            border: none;
        }
        
        &:after {
            background-color: ${theme.title === 'dark' ? theme.semantic.surface.primary : theme.semantic.surface.secondary} !important;
        }

        ${hoverable(css`
            color: ${theme.semantic.text.secondary} !important;
            && .colorFill {
                fill: ${theme.semantic.text.secondary} !important;
            }
            && .colorStroke {
                stroke: ${theme.semantic.text.secondary} !important;
            }
        `)}

        &:before, &:after {
            transition: none;
            transform: none;
            opacity: 1;
        }

        ${hoverable(css`
            transition: none;
            transform: none;
            opacity: 1;
        `, ':before')}

        ${hoverable(css`
            transition: none;
            transform: none;
            opacity: 1;
        `, ':after')}
    `}

    ${({ accent }) => accent && css`
        &:before {
            border-color: ${({ theme }: { theme: DefaultTheme }) => theme.brand?.error ?? primitiveColors.error[500]};
        }
        
        &:after {
            background-color: ${({ theme }: { theme: DefaultTheme }) => theme.brand?.error ?? primitiveColors.error[500]};
        }

        ${hoverable(css`
            color: ${({ theme }: { theme: DefaultTheme }) => theme.brand?.error ?? primitiveColors.error[500]};
            && .colorFill {
                fill: ${({ theme }: { theme: DefaultTheme }) => theme.brand?.error ?? primitiveColors.error[500]};
            }
            && .colorStroke {
                stroke: ${({ theme }: { theme: DefaultTheme }) => theme.brand?.error ?? primitiveColors.error[500]};
            }
        `)}
    `}
    
    ${({ line }) => line && css`
      border: 3px solid ${primitiveColors.gray[50]};
      
        &:before {
            border: none;
        }
        
        &:after {
            background: none;
        }

        ${hoverable(css`
            opacity: 0.5;
            color: ${primitiveColors.gray[50]};
            && .colorFill {
                fill: ${primitiveColors.gray[50]};
            }
            && .colorStroke {
                stroke: ${primitiveColors.gray[50]};
            }
        `)}
    `}

    ${({ transparent }) => transparent && css`
        &:before {
            border-color: ${primitiveColors.gray[50]};
        }
        
        &:after {
            background-color: ${primitiveColors.alpha.black25};
        }

        ${hoverable(css`
            color: ${primitiveColors.gray[50]};
            && .colorFill {
                fill: ${primitiveColors.gray[50]};
            }
            && .colorStroke {
                stroke: ${primitiveColors.gray[50]};
            }
        `)}
    `}
    
    ${({ theme, secondary }) => secondary && css`
        &:before {
            border-color:  ${theme.semantic.text.primary};
        }
        
        &:after {
            background-color:  ${theme.title === 'dark' ? theme.semantic.surface.primary : theme.semantic.text.primary};
        }

        ${hoverable(css`
            color:  ${theme.semantic.text.primary};
            && .colorFill {
                fill:  ${theme.semantic.text.primary};
            }
            && .colorStroke {
                stroke:  ${theme.semantic.text.primary};
            }
        `)}
    `}

    ${({ light, success }) => (light || success )&& css`
        &:before {
            border:  none;
        }

        ${hoverable(css`
            color:  ${primitiveColors.gray[50]};
            && .colorFill {
                fill:  ${primitiveColors.gray[50]};
            }
            && .colorStroke {
                stroke:  ${primitiveColors.gray[50]};
            }
        `)}
    `}
    

    ${({ light }) => light && css`
        color:  ${({ theme }: { theme: DefaultTheme }) => theme.brand?.primary ?? primitiveColors.primary[500]};

        && .colorFill {
            fill:  ${({ theme }: { theme: DefaultTheme }) => theme.brand?.primary ?? primitiveColors.primary[500]};
        }
      
        && .colorStroke {
            stroke:  ${({ theme }: { theme: DefaultTheme }) => theme.brand?.primary ?? primitiveColors.primary[500]};
        }
        
        &:after {
            background-color:  ${({ theme }: { theme: DefaultTheme }) => theme.brand?.primaryTransparent ?? primitiveColors.alpha.primary20};
        }

        ${hoverable(css`
            background-color:  ${({ theme }: { theme: DefaultTheme }) => theme.brand?.primary ?? primitiveColors.primary[500]};
        `)}
    `}

    ${({ success }) => success && css`
        color: ${({ theme }: { theme: DefaultTheme }) => theme.brand?.secondaryDark ?? primitiveColors.secondary[600]};
        
        && .colorFill {
            fill: ${({ theme }: { theme: DefaultTheme }) => theme.brand?.secondaryDark ?? primitiveColors.secondary[600]};
        }
        && .colorStroke {
            stroke: ${({ theme }: { theme: DefaultTheme }) => theme.brand?.secondaryDark ?? primitiveColors.secondary[600]};
        }
        
        &:after {
            background-color: ${({ theme }: { theme: DefaultTheme }) => theme.brand?.secondaryTransparent ?? primitiveColors.alpha.secondary20};
        }

        ${hoverable(css`
            background-color: ${({ theme }: { theme: DefaultTheme }) => theme.brand?.secondary ?? primitiveColors.secondary[500]};
        `)}
    `}
    
    

    ${({ middle }) => middle && css`
        height: 6rem;
        gap: 1rem;
        padding: 0 ${(p) => p.theme.primitives?.spacing?.[2] ?? padding.small};
    `}

    ${({ small }) => small && css`
        font-size: 2rem;
        height: 5rem;
        gap: 0.5rem;
        padding: 0 ${(p) => p.theme.primitives?.spacing?.[2] ?? padding.small};
    `}

    ${({ link }) => link && css`
        color: ${({ theme }: { theme: DefaultTheme }) => theme.brand?.primary ?? primitiveColors.primary[500]};
      
        && .colorFill {
            fill: ${({ theme }: { theme: DefaultTheme }) => theme.brand?.primary ?? primitiveColors.primary[500]};
        }
      
        && .colorStroke {
            stroke: ${({ theme }: { theme: DefaultTheme }) => theme.brand?.primary ?? primitiveColors.primary[500]};
        }
      
        &:before {
            border-color: transparent;
        }
        
        &:after {
            background-color: transparent;
        }

        ${hoverable(css`
            color: ${({ theme }: { theme: DefaultTheme }) => theme.brand?.primaryLight ?? primitiveColors.primary[400]};
            && .colorFill {
                fill: ${({ theme }: { theme: DefaultTheme }) => theme.brand?.primaryLight ?? primitiveColors.primary[400]};
            }
            && .colorStroke {
                stroke: ${({ theme }: { theme: DefaultTheme }) => theme.brand?.primaryLight ?? primitiveColors.primary[400]};
            }
        `)}
    `}
    
    
    ${({ dotted, theme }) => dotted && css`
        border: 2px dotted ${theme.semantic.surface.secondary};
        color: ${({ theme }: { theme: DefaultTheme }) => theme.brand?.primary ?? primitiveColors.primary[500]};
      
        && .colorFill {
            fill: ${({ theme }: { theme: DefaultTheme }) => theme.brand?.primary ?? primitiveColors.primary[500]};
        }
      
        && .colorStroke {
            stroke: ${({ theme }: { theme: DefaultTheme }) => theme.brand?.primary ?? primitiveColors.primary[500]};
        }
      
        &:before {
            border-color: transparent;
        }
        
        &:after {
            background-color: transparent;
        }

        ${hoverable(css`
            color: ${({ theme }: { theme: DefaultTheme }) => theme.brand?.primaryLight ?? primitiveColors.primary[400]};
            && .colorFill {
                fill: ${({ theme }: { theme: DefaultTheme }) => theme.brand?.primaryLight ?? primitiveColors.primary[400]};
            }
            && .colorStroke {
                stroke: ${({ theme }: { theme: DefaultTheme }) => theme.brand?.primaryLight ?? primitiveColors.primary[400]};
            }
        `)}
    `}
`;

const InnerWrap = styled.span`
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    width: fit-content;
    max-width: 100%;
    margin: 0;
    /* line-height: 1 + overflow: hidden = коробка ровно в строку: латинице её
       хватает (чернила 1.17em), а деванагари/арабица (1.23-1.30em) режутся по
       матрам и точкам. --lh-min поднимает минимум только для таких письменностей
       (см. bx/main.css); высоту кнопки задаёт она сама, не эта строка. */
    line-height: max(1em, var(--lh-min, 0px));
`;

const IconWrap = styled.span`
    display: inline-flex;
    transform: translateY(-1px); //оптическое выравнивание с иконкой / метриками BS
`;

const Button: React.FC<PropsType> = props => {
    const { children, href, type = 'button', ...restProps } = props;

    const map = React.Children.map(children, (child) => {
        if (typeof child === 'string') {
            return <InnerWrap>{child}</InnerWrap>;
        }

        if (React.isValidElement(child)) {
            return <IconWrap>{child}</IconWrap>;
        }

        return child;
    });

    if (href) {
        return <Wrap as="a" href={href} {...restProps}>{map}</Wrap>;
    }

    return <Wrap type={type} {...restProps}>{map}</Wrap>;
}

export default Button;
